/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FC, useCallback } from "react";
import { ISwitchProps, Switch } from "../../core";
import { FieldWrapper } from "../FieldWrapper";
import { isFunction } from "@adapptio/lib-shared";

export interface IFieldSwitchProps {
	value: boolean;
	disabled?: boolean;
	className?: string;
	tooltip?: string;
	readOnly?: boolean;
	onChange?: (value: boolean) => void;
}

export const FieldSwitch: FC<IFieldSwitchProps> = ({
	value,
	disabled,
	className,
	tooltip,
	onChange,
	readOnly
}) => {
	const handleChange = useCallback<ISwitchProps["onChange"]>(
		(event) => {
			event.stopPropagation();

			if (readOnly) {
				return;
			}

			const value = event.target.checked;

			if (isFunction(onChange)) {
				onChange(value);
			}
		},
		[ value, onChange, readOnly ]
	);

	const baseClass = BEM("edt-field-switch", null, {
		"state-on": value,
		"state-off": !value,
		readonly: readOnly
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<FieldWrapper type="switch">
			<Switch active={value} disabled={disabled || readOnly} onChange={handleChange} />
		</FieldWrapper>
	);
};
